import React from 'react';
import { useTranslation } from 'react-i18next';
import ReportStaticInfo from './ReportStaticInfo';
import { useHistory, useParams } from 'react-router-dom';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import Button from '@ssg/common/Components/Button';
import Box from '../../../Components/Layout/Box';
import Header from '@ssg/common/Components/Header';
import Loading from '@ssg/common/Components/Loading';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { faCheck, faFilePdf, faSync } from '@fortawesome/pro-regular-svg-icons';

import { DeviationColor, DeviationInput, useCreateDeviationReportMutation, useGetCaseAsbestosReportsAndDocumentsQuery } from '@ssg/common/GraphQL/indexV2';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import Radio from '@ssg/common/Components/Radio';
import TextAreaInput from './Inputs/TextAreaInput';

import TextInput from './Inputs/TextInput';
import Datepicker from '@ssg/common/Components/Datepicker';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';

const DeviationColorOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yellow', value: DeviationColor.Yellow },
	{ label: 'reports.asbestos.red', value: DeviationColor.Red },
];

interface DeviationError {
	key: keyof DeviationInput;
}

const EMPTY_DEVIATION_FORM = {
	reportDate: formatDateForInput(new Date()),
	action: '',
	actionDate: null,
	actionResponsible: '',
	cause: '',
	description: '',
	severityLevel: DeviationColor.NonSelected,
};

const throwDeviationError = (key: keyof DeviationInput, deviationErrors: DeviationError[]): string | undefined => {
	const isError = deviationErrors.filter(error => error.key === key).length > 0;
	if (isError) return 'error.required';
	return;
};

interface Params {
	id: string;
}

const DeviationReport: React.FC = () => {
	const { id: caseId } = useParams<Params>();

	const { t } = useTranslation();
	const history = useHistory();
	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	const { loading: caseReportsAndDocumentsLoading } = useGetCaseAsbestosReportsAndDocumentsQuery({
		variables: {
			id: caseId,
		},
		skip: !caseId || caseId === '',
		onCompleted(data) {
			const formData = removeTypenameGeneric(data.case.caseAsbestosReports?.deviationReportFormData);
			if (formData) {
				setDeviation(formData.deviation);
			}
		},
	});

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${caseId}-deviation-${name}`, initialValue);
	const [deviation, setDeviation] = useLocalStorage<DeviationInput>('deviationReport', EMPTY_DEVIATION_FORM);
	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');

	const [deviationErrors, setDeviationError] = React.useState<DeviationError[]>([]);

	const [createDeviationReport, { loading: submitting }] = useCreateDeviationReportMutation();

	const handleDeviationChange = (key: keyof DeviationInput, value: DeviationInput[keyof DeviationInput]) => {
		setDeviation(currentDeviation => ({
			...currentDeviation,
			[key]: value,
		}));
	};

	async function submitReport() {
		const hasErrors = errorCheck();
		if (hasErrors) {
			return;
		}
		try {
			await createDeviationReport({
				variables: {
					caseId,
					formData: { deviation },
					onlySave: false,
				},
			});

			history.goBack();
		} catch (e) {
			console.error(e);
		}
	}

	React.useEffect(() => {
		setSaved(false);
	}, []);

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createDeviationReport({
				variables: {
					caseId,
					formData: { deviation },
					onlySave: true,
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}
	const resetForm = () => {
		setDeviation(EMPTY_DEVIATION_FORM);
	};

	const errorCheck = (): boolean => {
		const deviationErrors: DeviationError[] = [];

		if (deviation.reportDate === '' || deviation.reportDate === 'NaN-aN-aN') {
			deviationErrors.push({ key: 'reportDate' });
		}
		if (deviation.severityLevel === DeviationColor.NonSelected) {
			deviationErrors.push({ key: 'severityLevel' });
		}
		if (deviation.description === '') {
			deviationErrors.push({ key: 'description' });
		}
		if (deviation.cause === '') {
			deviationErrors.push({ key: 'cause' });
		}

		setDeviationError(deviationErrors);

		return deviationErrors.length > 0;
	};

	if (caseReportsAndDocumentsLoading) return <Loading />;

	return (
		<div>
			<Header title="reports.reportTypes.deviation" backButton />

			<div className="space-y-8 px-10 pb-10 text-sm">
				<ReportStaticInfo caseId={caseId} />
				<Box full icon={faSync} iconText="reports.reset" title="" onClick={() => resetForm()}>
					<div className="text-2xl mt-4 font-bold text-blue">{t('reports.reportTypes.deviation')}</div>
					<div className="flex flex-wrap">
						<div className="w-full">
							<div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white shadow-lg">
								<div className="flex-auto px-4 py-5">
									<div className="tab-content tab-space">
										<div className="mt-4">
											<Datepicker
												title={'reports.asbestos.deviationReportDate'}
												name={'deviationReportDate'}
												value={deviation.reportDate}
												onChange={e => handleDeviationChange('reportDate', e.target.value)}
												errorMessage={throwDeviationError('reportDate', deviationErrors)}
											/>
											<Radio
												name={'severityLevel'}
												options={DeviationColorOptions}
												title={'reports.asbestos.deviationSeverity'}
												onChange={e => {
													const color = e.target.value as DeviationColor;
													handleDeviationChange('severityLevel', color);
												}}
												value={deviation.severityLevel}
												horizontal
												errorMessage={throwDeviationError('severityLevel', deviationErrors)}
											/>
											<TextAreaInput
												label="reports.asbestos.description"
												value={deviation.description}
												onChange={e => handleDeviationChange('description', e)}
												placeholder={'reports.asbestos.descriptionPlaceholder'}
												errorMessage={throwDeviationError('description', deviationErrors)}
											/>
											<TextAreaInput
												label="reports.asbestos.cause"
												value={deviation.cause}
												onChange={e => handleDeviationChange('cause', e)}
												placeholder={'reports.asbestos.causePlaceholder'}
												errorMessage={throwDeviationError('cause', deviationErrors)}
											/>
											<TextAreaInput
												label="reports.asbestos.correctingAction"
												value={deviation.action}
												onChange={e => handleDeviationChange('action', e)}
												placeholder={'reports.asbestos.actionPlaceholder'}
											/>
											<TextInput label={'reports.asbestos.actionResponsible'} value={deviation.actionResponsible} onChange={e => handleDeviationChange('actionResponsible', e)} />
											<Datepicker
												title={'reports.asbestos.actionDate'}
												name={'actionDate'}
												value={deviation.actionDate ?? ''}
												onChange={e =>
													e.target.value === '' || e.target.value === 'NaN-aN-aN'
														? handleDeviationChange('actionDate', null)
														: handleDeviationChange('actionDate', e.target.value)
												}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Box>

				<div className="ml-4 my-2 mr-6 flex flex-row space-x-2">
					<Button icon={faFilePdf} onClick={() => submitReport()} disabled={submitting} loading={submitting && loadingType === 'CREATE'} success text="reports.createPdfReport" />

					{saveButtonReportsFlag && (
						<Button
							disabled={submitting}
							loading={submitting && loadingType === 'SAVE'}
							primary
							onClick={() => saveReport()}
							text={saved ? '' : 'reports.saveReport'}
							icon={saved ? faCheck : undefined}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default DeviationReport;
